import { X, ArrowUpCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useProfile } from '../../../../../context/profile/ProfileContext';
import { profileService } from '../../../../../services/profile/profileService';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import subscriptionData from '../../../../../data/subscription/subscriptionData.json';

const annualReasonOptions = subscriptionData.modalReasonOptions?.annualUpgrade || [];

const AnnualSupportModal = () => {
  const {
    subscription,
    showAnnualSupportModal,
    setShowAnnualSupportModal,
    selectedUpgradePlan,
    requestReason,
    setRequestReason,
    requestComments,
    setRequestComments,
    isSubmittingRequest,
    setIsSubmittingRequest,
    requestSuccessMessage,
    setRequestSuccessMessage,
    refreshSubscription,
    formatCurrency
  } = useProfile();

  if (!showAnnualSupportModal) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto"
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl border border-slate-100 overflow-hidden animate-modal-entry">
        {requestSuccessMessage ? (
          <div className="p-7 text-center space-y-4">
            <div className="w-16 h-16 bg-teal-50 text-[#067978] rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 size={36} />
            </div>
            <h2 className="font-bold text-lg text-slate-900">Request Received</h2>
            <p className="text-xs text-slate-600 leading-relaxed max-w-sm mx-auto">
              {requestSuccessMessage}
            </p>
            <button
              onClick={() => {
                setShowAnnualSupportModal(false);
                setRequestSuccessMessage(null);
              }}
              className="w-full py-2.5 bg-[#067978] hover:bg-[#05605f] text-white text-xs font-bold rounded-xl shadow-md shadow-[#067978]/20 transition-all cursor-pointer"
            >
              Got It
            </button>
          </div>
        ) : (
          <>
            <div className="p-6 pb-4 flex justify-between items-start border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-teal-50 text-[#067978] flex items-center justify-center shrink-0">
                  <ArrowUpCircle size={22} />
                </div>
                <div>
                  <h2 className="font-bold text-base text-slate-900">
                    Annual Plan Upgrade Request
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">Submit request for admin coordination</p>
                </div>
              </div>
              <button onClick={() => setShowAnnualSupportModal(false)} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 cursor-pointer">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 pt-4 space-y-4">
              {selectedUpgradePlan && (
                <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 space-y-2">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500 font-normal">Target Annual Plan</span>
                    <b className="font-semibold text-slate-800">{selectedUpgradePlan.name} Tier</b>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500 font-normal">Annual Rate</span>
                    <b className="font-semibold text-[#067978]">
                      {formatCurrency(selectedUpgradePlan.yearly_price || Math.round(selectedUpgradePlan.monthly_price * 12 * 0.80))} / year + 18% GST
                    </b>
                  </div>
                </div>
              )}

              <div>
                <CustomSelect
                  label="Reason for request"
                  value={requestReason}
                  onChange={(val) => setRequestReason(val)}
                  options={annualReasonOptions}
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">Comments (optional)</label>
                <textarea
                  rows={2}
                  value={requestComments}
                  onChange={(e) => setRequestComments(e.target.value)}
                  placeholder="Any specific note for the Trrip admin team..."
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-normal text-slate-700 transition-all outline-none focus:border-primary focus:ring-1 focus:ring-primary resize-none shadow-sm"
                />
              </div>

              <div className="flex gap-3 justify-end pt-2 border-t border-slate-100">
                <button
                  onClick={() => setShowAnnualSupportModal(false)}
                  className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-600 rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setIsSubmittingRequest(true);
                    try {
                      await profileService.createSubscriptionRequest({
                        type: 'upgrade',
                        target_plan_id: selectedUpgradePlan?._id || subscription?.plan_id?._id,
                        target_billing_interval: 'year',
                        reason: requestReason,
                        comments: requestComments
                      });
                      setRequestSuccessMessage('Your request to upgrade your Annual Subscription has been submitted. Our team will coordinate with you in 24–48 hours.');
                      refreshSubscription();
                    } catch (err) {
                      toast.error(err.response?.data?.error || err.message || 'Failed to submit request');
                    } finally {
                      setIsSubmittingRequest(false);
                    }
                  }}
                  disabled={isSubmittingRequest}
                  className="px-4 py-2 bg-[#067978] hover:bg-[#05605f] text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-[#067978]/20 flex items-center justify-center cursor-pointer disabled:opacity-60"
                >
                  {isSubmittingRequest ? (
                    <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span>Submit Request</span>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnnualSupportModal;
